import { useState } from 'react';
import { useTracking } from 'react-tracking';
import { usePriceHistoryData } from '@components/MiniPriceChart';
import useUser from '@/hooks/useUser';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { NarrativeFairValueModal } from '@/components/NarrativeFairValue';
import { useExistingNarrative } from '@/hooks/narrative/useExistingNarrative';
import GuestRegistrationModal from '@components/GuestRegistrationModal';
import { CURRENT_PERIOD } from '../../constants';
import { PopulatedState } from './PopulatedState';
import { NarrativePanelRegister } from '@/components/GuestRegistrationModal/components/NarrativePanelRegister';

type Props = {
  companyId: string;
  sharePrice: number | null;
  fairValue: number | null;
};

export const NarrativeKeyMetric = ({
  companyId,
  sharePrice,
  fairValue,
}: Props) => {
  const { trackEvent } = useTracking({ subject: 'primary_narrative' });
  const { data: user } = useUser();
  const { data } = usePriceHistoryData(companyId, CURRENT_PERIOD);

  const lastSharePrice = data.entities[data.keys[data.keys.length - 1]];
  const price = sharePrice ?? lastSharePrice;

  const { data: existingNarrative, status } = useExistingNarrative({
    companyId,
    loggedIn: user.loggedIn,
  });

  const targetFairValue =
    fairValue ?? existingNarrative?.Company.myValuation?.fairValue;
  const [isNarrativeFairValueModalOpen, setNarrativeFairValueModal] =
    useState(false);
  const [isGuestRegistrationModalOpen, setGuestRegistrationModal] =
    useState(false);

  const narrativeId = existingNarrative?.Company.myValuation?.narrative?.id;

  const handleOnClick = () => {
    trackEvent({
      action: 'click',
      ...(narrativeId && { modifier: 'edit' }),
    });

    if (user.loggedIn) {
      setNarrativeFairValueModal(true);
    } else {
      setGuestRegistrationModal(true);
    }
  };

  if (status === 'pending' || status === 'error') {
    return <div>{/* Placeholder div to prevent CLS */}</div>;
  }

  const currencyISO = getTradingItemCurrencyISO(
    existingNarrative.Company.data.currencyInfo
  );
  const { analysisValue, myValuation } = existingNarrative.Company ?? {};

  return (
    <>
      <PopulatedState
        owner={myValuation?.narrative?.owner}
        narrativeId={narrativeId}
        currencyISO={currencyISO}
        sharePrice={price}
        npvPerShare={analysisValue?.npvPerShare ?? null}
        fairValue={targetFairValue ?? null}
        onClick={handleOnClick}
      />
      <NarrativeFairValueModal
        isModalOpen={isNarrativeFairValueModalOpen}
        onClose={() => setNarrativeFairValueModal(false)}
        companyId={companyId}
      />
      {isGuestRegistrationModalOpen && (
        <GuestRegistrationModal
          canClose
          onClose={() => setGuestRegistrationModal(false)}
          removeWindowScroll
          onLoginSuccess={() => setGuestRegistrationModal(false)}
          onRegisterSuccess={() => setGuestRegistrationModal(false)}
          SplashPanel={{
            Component: NarrativePanelRegister,
            isDark: true,
          }}
        />
      )}
    </>
  );
};
